// deployment_class: customer_env_allowed
/**
 * Surviving a deploy with the tab still open (UI-B5, client half).
 *
 * ── The failure ────────────────────────────────────────────────────────────
 * A tab holds the entry bundle from build N. Build N+1 ships, and every chunk
 * hash changes. The moment that tab navigates to a lazily-loaded route, it asks
 * for a chunk that no longer exists and Vite throws
 * `Failed to fetch dynamically imported module`. The route never renders.
 *
 * The server half of UI-B5 makes that request answer 404 instead of 200 HTML,
 * which stops the browser parsing HTML as JavaScript. But a 404 is still a dead
 * route to the person looking at it — the board is explicit that neither half is
 * sufficient alone. This is the other half: the tab reloads itself once and
 * comes back on the current build.
 *
 * ── Why a reload is the right answer, and the only one ──────────────────────
 * The missing chunk is genuinely gone. Nothing can fetch it, no retry helps, and
 * there is no cache to clear — the fix is to pick up the new entry bundle, which
 * is what a reload does. Anything cleverer would be pretending there is a
 * recovery path that does not exist.
 *
 * ── Why it CANNOT loop, which is the part that matters ──────────────────────
 * A naive "reload on failure" is a boot loop when the failure survives the
 * reload — a broken deploy would spin a customer's tab forever, and a loop is
 * far worse than the dead route it replaces. So a reload is recorded with a
 * timestamp and a second one inside `RELOAD_COOLDOWN_MS` is REFUSED: the error
 * surfaces instead, which is honest and finite.
 *
 * A timestamp rather than a one-shot boolean, deliberately. A boolean that is
 * never cleared disarms recovery permanently after the first deploy; a boolean
 * cleared on successful boot re-arms instantly and loops. A cooldown window does
 * neither — it bounds the rate without ever giving the capability up.
 *
 * `sessionStorage` is per-tab, which is the correct scope: this is one tab's
 * staleness, not the browser's. Every access is guarded — a private window or a
 * blocked-storage setting throws on read, and a boot path that throws in a
 * privacy mode is worse than one that simply does not recover.
 */

/** Key under which the last self-reload is recorded, per tab. */
export const RELOAD_MARK_KEY = 'kd:preload-reload-at';

/**
 * How long a recorded reload suppresses another.
 *
 * Long enough that a genuinely broken build cannot spin (a boot-to-failure cycle
 * is well under a second), short enough that a tab left open across two separate
 * deploys can still recover from the second.
 */
export const RELOAD_COOLDOWN_MS = 10_000;

/** Read the mark; treat any storage failure as "no mark". */
function readMark(storage: Storage | undefined): number | null {
  try {
    const raw = storage?.getItem(RELOAD_MARK_KEY);
    if (raw === null || raw === undefined) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  } catch {
    return null;
  }
}

function writeMark(storage: Storage | undefined, at: number): void {
  try {
    storage?.setItem(RELOAD_MARK_KEY, String(at));
  } catch {
    // Storage unavailable. The reload below still happens; it simply is not
    // rate-limited in this tab. Failing the boot over a telemetry-shaped write
    // would be the worse trade.
  }
}

export interface PreloadRecoveryDeps {
  readonly storage: Storage | undefined;
  readonly now: () => number;
  readonly reload: () => void;
}

/**
 * Decide and act on one preload failure.
 *
 * Exported separately from the listener so the decision is testable without
 * dispatching real events or navigating a real document.
 *
 * @returns whether a reload was performed — so a caller (and a test) can tell
 *          "recovered" from "refused, surfacing the error".
 */
export function handlePreloadError(deps: PreloadRecoveryDeps): boolean {
  const last = readMark(deps.storage);
  const now = deps.now();

  // Already reloaded recently: the reload did not fix it, so doing it again
  // would spin. Let the failure surface.
  if (last !== null && now - last < RELOAD_COOLDOWN_MS) return false;

  writeMark(deps.storage, now);
  deps.reload();
  return true;
}

/**
 * Listen for Vite's preload failure and recover once.
 *
 * `vite:preloadError` is emitted on `window` by Vite's own dynamic-import
 * helper. Calling `preventDefault()` marks it handled so Vite does not also
 * rethrow it as an unhandled rejection — we are answering it.
 *
 * @returns an unsubscribe function (used by tests; the app never unsubscribes).
 */
export function installPreloadRecovery(
  deps: PreloadRecoveryDeps = {
    storage: typeof sessionStorage === 'undefined' ? undefined : sessionStorage,
    now: () => Date.now(),
    reload: () => {
      window.location.reload();
    },
  },
): () => void {
  const onPreloadError = (event: Event): void => {
    if (handlePreloadError(deps)) event.preventDefault();
  };

  window.addEventListener('vite:preloadError', onPreloadError);
  return () => {
    window.removeEventListener('vite:preloadError', onPreloadError);
  };
}
